// Chakra imports
import { ChakraProvider, Portal, useDisclosure, Flex, Text, Button, Menu, MenuButton, MenuList, MenuItem, MenuGroup, MenuDivider, Box, HStack, Avatar, Badge, Icon, useToast, useColorModeValue } from '@chakra-ui/react';
import Configurator from 'components/Configurator/Configurator';
import Footer from 'components/Footer/Footer.js';
// Layout components
import AdminNavbar from 'components/Navbars/AdminNavbar.js';
import Sidebar from 'components/Sidebar';
import React, { useState, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import routes from 'routes.js';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
// Custom Chakra theme
import theme from 'theme/theme.js';
// Custom components
import MainPanel from '../components/Layout/MainPanel';
import PanelContainer from '../components/Layout/PanelContainer';
import PanelContent from '../components/Layout/PanelContent';
import { useAuth } from "auth/authProvider";
import { useHistory } from "react-router-dom";
import { FiPower, FiStar } from 'react-icons/fi';
import UpgradeModel from '../payments/upgradeModel.js';
import { ChevronDownIcon } from '@chakra-ui/icons';

export default function Dashboard(props) {
	const { ...rest } = props;
	const [ sidebarVariant, setSidebarVariant ] = useState('transparent');
	const [ fixed, setFixed ] = useState(false);
	const [showUpgrade, setShowUpgrade] = useState(false);
	
	const getRoute = () => {
		return window.location.pathname !== '/admin/full-screen-maps';
	};
	
	const getActiveRoute = (routes) => {
		let activeRoute = 'Default Brand Text';
		for (let i = 0; i < routes.length; i++) {
			if (routes[i].collapse) {
				let collapseActiveRoute = getActiveRoute(routes[i].views);
				if (collapseActiveRoute !== activeRoute) {
					return collapseActiveRoute;
				}
			} else if (routes[i].category) {
				let categoryActiveRoute = getActiveRoute(routes[i].views);
				if (categoryActiveRoute !== activeRoute) {
					return categoryActiveRoute;
				}
			} else {
				if (window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1) {
					return routes[i].name;
				}
			}
		}
		return activeRoute;
	};
	
	// This changes navbar state(fixed or not)
	const getActiveNavbar = (routes) => {
		let activeNavbar = false;
		for (let i = 0; i < routes.length; i++) {
			if (routes[i].category) {
				let categoryActiveNavbar = getActiveNavbar(routes[i].views);
				if (categoryActiveNavbar !== activeNavbar) {
					return categoryActiveNavbar;
				}
			} else {
				if (window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1) {
					if (routes[i].secondaryNavbar) {
						return routes[i].secondaryNavbar;
					}
				}
			}
		}
		return activeNavbar;
	};
	
	const getRoutes = (routes) => {
		return routes.map((prop, key) => {
			if (prop.collapse) {
				return getRoutes(prop.views);
			}
			if (prop.category === 'account') {
				return getRoutes(prop.views);
			}
			if (prop.layout === '/admin') {
				return <Route path={prop.layout + prop.path} component={prop.component} key={key} />;
			} else {
				return null;
			}
		});
	};
	
	const { isOpen, onOpen, onClose } = useDisclosure();
	document.documentElement.dir = 'ltr';
	const history = useHistory();
	const toast = useToast();
	const { user, logout, subscription, refreshSubscription } = useAuth();
	
	useEffect(() => {
		const cancelPending = !!localStorage.getItem('subscriptionCancelPending');
		const updatePending = !!localStorage.getItem('subscriptionUpdatePending');
		const portalPending = !!localStorage.getItem('subscriptionPortalPending');
	
		if (cancelPending || updatePending || portalPending) {
			localStorage.removeItem('subscriptionCancelPending');
			localStorage.removeItem('subscriptionUpdatePending');
			localStorage.removeItem('subscriptionPortalPending');
	
			refreshSubscription()
				.then(() => {
					if (cancelPending)
						toast({
							title: 'Your subscription is scheduled to end at the next billing period. Manage your subscription to renew or subscribe to a new plan.',
							status: 'info',
							position: 'top',
							duration: 4000,
							isClosable: true
						});
					else if (updatePending)
						toast({
							title: 'Subscription updated',
							status: 'success',
							position: 'top',
							duration: 4000,
							isClosable: true
						});
				})
				.catch(err =>
					toast({
						title: 'Error refreshing subscription',
						description: err.message,
						status: 'error',
						position: 'top',
						duration: 4000,
						isClosable: true
					})
				);
		}
	}, [refreshSubscription, toast, showUpgrade]);
	
	const handleSignOut = () => {
		logout();
		history.replace('/');
	};

	const secondaryNavbar = getActiveNavbar(routes);
	
	const planMap = {
		'price_1RlAZZFYj9GidIJrWLc3x5DH': 'Basic',
		'price_1RlAYWFYj9GidIJrPgK15O04': 'Pro',
		'price_1RlAa3FYj9GidIJrBscks66Z': 'Enterprise'
	};
	const currentPriceId = subscription?.[0]?.items?.data?.[0]?.price?.id;
	const planName = planMap[currentPriceId] || 'Basic';
	
	const cmv = useColorModeValue;
	const authMenu = (
		<Menu isLazy placement="bottom-end">
			<MenuButton as={Button} variant="ghost" size="sm" _hover={{ bg: 'transparent' }} _active={{ bg: 'transparent' }} _focus={{ boxShadow: 'none' }}>
				<HStack spacing={3}>
					<Text fontSize="sm" color={cmv('gray.700', 'white')}>
						<Text as="span" fontWeight="normal">Welcome back, </Text>
						<Text as="span" fontWeight="semibold">{user?.username}</Text>
					</Text>
					<Box pos="relative">
						<Avatar name={user?.username} size="sm" bg="blue.200" color="blue.800" />
						<Badge pos="absolute" top="100%" left="50%" transform="translate(-50%, -25%)" colorScheme="purple" variant="subtle" fontSize="0.65rem" textTransform="none">
							{planName}
						</Badge>
					</Box>
					<ChevronDownIcon />
				</HStack>
			</MenuButton>
	
			<MenuList
				bg={cmv('white', 'gray.800')}
				border="1px solid"
				borderColor={cmv('blue.100', 'gray.700')}
				boxShadow="md"
				borderRadius="md"
				py={1}
				minW="160px"
			>
				<MenuGroup title="Account" fontSize="xs" color="gray.500">
					<MenuItem
						fontSize="sm" 
						py={2} 
						px={3} 
						transition="background 0.15s, color 0.15s"
						onClick={() => setShowUpgrade(true)}
					>
						<HStack spacing={3}>
							<Icon as={FiStar} boxSize={4} color={cmv('blue.600', 'blue.300')} />
							<Text>Subscription</Text>
						</HStack>
					</MenuItem>
	
					<MenuDivider />
	
					<MenuItem
						fontSize="sm" 
						py={2} 
						px={3} 
						transition="background 0.15s, color 0.15s"
						onClick={handleSignOut}
					>
						<HStack spacing={3}>
							<Icon as={FiPower} boxSize={4} color={cmv('red.500', 'red.300')} />
							<Text>Sign Out</Text>
						</HStack>
					</MenuItem>
				</MenuGroup>
			</MenuList>
		</Menu>
	);

	return (
		<ChakraProvider theme={theme} resetCss={false}>
			{showUpgrade && (
				<UpgradeModel
					onClose={() => setShowUpgrade(false)}
					onUpgrade={() => setShowUpgrade(false)}
					userId={user?.id}
					email={user?.email}
				/>
			)}
			
			<Sidebar
				routes={routes}
				logoText={'chatbot UI DASHBOARD'}
				display='none'
				sidebarVariant={sidebarVariant}
				{...rest}
			/>
			<MainPanel
				w={{
					base: '100%',
					xl: 'calc(100% - 275px)'
				}}>
				<Portal>
					<AdminNavbar
						onOpen={onOpen}
						logoText={'chatbot UI DASHBOARD'}
						brandText={getActiveRoute(routes)}
						secondary={secondaryNavbar}
						fixed={fixed}
						authMenu={authMenu}
						{...rest}
					/>
				</Portal>
				{getRoute() ? (
					<PanelContent>
						<PanelContainer>
							<Switch>
								{getRoutes(routes)}
								<Redirect from='/admin' to='/admin/dashboard' />
							</Switch>
						</PanelContainer>
					</PanelContent>
				) : null}
				<Footer />
				<Configurator
					secondary={getActiveNavbar(routes)}
					isOpen={isOpen}
					onClose={onClose}
					isChecked={fixed}
					onSwitch={(value) => {
						setFixed(value);
					}}
					onOpaque={() => setSidebarVariant('opaque')}
					onTransparent={() => setSidebarVariant('transparent')}
				/>
			</MainPanel>
		</ChakraProvider>
	);
}